import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Button,
  Tag,
  List,
  Tooltip,
  Space,
  Typography,
  Row,
  Col,
  Modal,
  Radio,
} from "antd";
import { PlayCircle, Pencil, Trash2, Link, FileText } from "lucide-react";
import cronstrue from "cronstrue";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import apiClient from "@/services/apiClient";
import { BACKEND_URL } from "@/config";
import "./CrawlCard.css";

const { Text } = Typography;

const CrawlConfigCard = ({ cfg, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [useSonyCrawler, setUseSonyCrawler] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // null | "success" | "error"
  const [errorMessage, setErrorMessage] = useState(null);
  const [logModalVisible, setLogModalVisible] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [fullLogModal, setFullLogModal] = useState({
    visible: false,
    content: "",
  });
  const sseRef = useRef(null);

  const handlePlay = () => {
    setModalVisible(true);
    setUseSonyCrawler(false);
    setResult(null);
    setErrorMessage(null);
  };

  const handleConfirmCrawl = async () => {
    setLoading(true);
    setResult(null);
    setErrorMessage(null);
    try {
      const response = await apiClient.post(
        `${BACKEND_URL}/api/v1/content-ingestion/crawl/start`,
        {
          client_id: cfg.client_id,
          use_sony_crawler: useSonyCrawler,
        },
      );
      const data = response.data;
      cfg.last_crawl_job_id = data.job_id;
      setResult("success");
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || err.message);
      setResult("error");
    } finally {
      setLoading(false);
    }
  };

  const handleSeeLogs = () => {
    setLogModalVisible(true);
    setLogs([]);
    setLogLoading(true);
    startSSE();
    setModalVisible(false);
    setResult(null);
  };

  const startSSE = () => {
    const es = new window.EventSource(
      `${BACKEND_URL}/api/v1/content-ingestion/crawl/status/${encodeURIComponent(cfg.last_crawl_job_id)}`,
      { withCredentials: true },
    );
    sseRef.current = es;
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        setLogs((prev) => [...prev, data]);
      } catch {
        setLogs((prev) => [...prev, e.data]);
      }
      setLogLoading(false);
    };
    es.onerror = () => {
      setLogLoading(false);
      es.close();
    };
  };

  const handleCloseLogModal = () => {
    setLogModalVisible(false);
    setLogs([]);
    setLogLoading(false);
    if (sseRef.current) {
      sseRef.current.close?.();
      sseRef.current = null;
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setUseSonyCrawler(false);
    setResult(null);
    setErrorMessage(null);
    setLoading(false);
  };

  return (
    <>
      <Card
        title={
          <Row align="middle" gutter={8}>
            <Col>
              <Tag color="magenta" className="crawlconfig-client-tag">
                {cfg.client_id}
              </Tag>
            </Col>
          </Row>
        }
        extra={
          <Space size={0}>
            <Tooltip
              title={t("superAdmin.contentIngestion.startCrawl", "Start Crawl")}
            >
              <Button
                type="text"
                icon={<PlayCircle size={20} />}
                onClick={handlePlay}
                style={{ marginRight: 0 }}
              />
            </Tooltip>
            {cfg.last_crawl_job_id && (
              <Tooltip
                title={t("superAdmin.contentIngestion.viewLogs", "View Logs")}
              >
                <Button
                  type="text"
                  icon={<FileText size={20} />}
                  onClick={handleSeeLogs}
                  style={{ marginRight: 0 }}
                />
              </Tooltip>
            )}
            <Tooltip title={t("superAdmin.contentIngestion.edit", "Edit")}>
              <Button
                type="text"
                icon={<Pencil size={20} />}
                onClick={() => onEdit(cfg)}
                style={{ marginRight: 0 }}
              />
            </Tooltip>
            <Tooltip title={t("superAdmin.contentIngestion.delete", "Delete")}>
              <Button
                type="text"
                icon={<Trash2 size={20} color="#ff4d4f" />}
                danger
                onClick={() => onDelete(cfg)}
              />
            </Tooltip>
          </Space>
        }
        className="crawlconfig-card"
        variant="outlined"
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div className="mt-3 mb-0">
            <Text strong>
              {t("superAdmin.contentIngestion.elasticIndex", "Elastic Index")}
            </Text>
            <div>
              <Tag
                color="orange"
                className="crawlconfig-index-tag"
                style={{ marginTop: 4 }}
              >
                {cfg.config.output_index}
              </Tag>
            </div>
            <Text strong style={{ display: "block", marginTop: 12 }}>
              {t(
                "superAdmin.contentIngestion.crawlFrequency",
                "Crawl Frequency",
              )}
            </Text>
            <div>
              <Tag
                color="blue"
                className="crawlconfig-frequency-tag"
                style={{ marginBottom: 2, marginTop: 4 }}
              >
                {(() => {
                  try {
                    return cfg.crawl_frequency &&
                      cfg.crawl_frequency.trim() !== ""
                      ? cronstrue.toString(cfg.crawl_frequency)
                      : t("superAdmin.contentIngestion.notSet", "Not Set");
                  } catch (e) {
                    return t(
                      "superAdmin.contentIngestion.invalidFrequency",
                      "Invalid Frequency",
                    );
                  }
                })()}
              </Tag>
            </div>
            {(cfg.last_crawl_job_id || cfg.last_crawled_at) && (
              <div className="crawlconfig-last-crawl-info">
                {cfg.last_crawl_job_id && (
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    <span>
                      {t(
                        "superAdmin.contentIngestion.lastCrawlJobId",
                        "Last Crawl Job ID",
                      )}
                      :
                    </span>
                    <Tag color="purple">{cfg.last_crawl_job_id}</Tag>
                  </Text>
                )}
                {cfg.last_crawled_at && (
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    <span>
                      {t(
                        "superAdmin.contentIngestion.lastCrawledAt",
                        "Last Crawled At",
                      )}
                      :
                    </span>
                    <Tag color="geekblue">
                      {dayjs(cfg.last_crawled_at).format("YYYY-MM-DD HH:mm:ss")}
                    </Tag>
                  </Text>
                )}
              </div>
            )}
          </div>
          <div>
            <Text strong>
              {t("superAdmin.contentIngestion.domain", "Domain")}
            </Text>
            <List
              size="small"
              dataSource={cfg.config.domains}
              renderItem={(domain) => (
                <List.Item className="crawlconfig-domain-listitem">
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Row align="middle" gutter={8} wrap={false}>
                      <Col>
                        <Link size={16} />
                      </Col>
                      <Col flex="auto">
                        <Text>{domain.url}</Text>
                      </Col>
                      {domain.sitemap_urls?.length > 0 && (
                        <Col>
                          <Tag
                            color="green"
                            className="crawlconfig-domain-sitemaps-tag"
                          >
                            {t(
                              "superAdmin.contentIngestion.sitemaps",
                              "Sitemaps",
                            )}
                            : {domain.sitemap_urls.length}
                          </Tag>
                        </Col>
                      )}
                    </Row>
                    {domain.crawl_rules?.length > 0 && (
                      <List
                        size="small"
                        header={
                          <Row align="middle" gutter={8}>
                            <Col>
                              <Text strong>
                                {t(
                                  "superAdmin.contentIngestion.crawlRules",
                                  "Crawl Rules",
                                )}
                              </Text>
                            </Col>
                          </Row>
                        }
                        dataSource={domain.crawl_rules}
                        renderItem={(rule) => (
                          <List.Item className="crawlconfig-rule-listitem">
                            <Row gutter={8} align="middle" wrap={false}>
                              <Col>
                                <Tag
                                  color={
                                    rule.policy === "allow" ? "blue" : "red"
                                  }
                                  className="crawlconfig-rule-policy-tag"
                                >
                                  {rule.policy}
                                </Tag>
                              </Col>
                              <Col>
                                <Tag className="crawlconfig-rule-type-tag">
                                  {rule.type}
                                </Tag>
                              </Col>
                              <Col>
                                <Tag
                                  color="magenta"
                                  className="crawlconfig-rule-pattern-tag"
                                >
                                  {rule.pattern}
                                </Tag>
                              </Col>
                            </Row>
                          </List.Item>
                        )}
                      />
                    )}
                  </Space>
                </List.Item>
              )}
            />
          </div>
        </Space>
      </Card>
      <Modal
        open={modalVisible}
        title={t("superAdmin.contentIngestion.startCrawl", "Start Crawl")}
        onCancel={handleCloseModal}
        footer={
          result === "success"
            ? [
                <Button key="logs" type="primary" onClick={handleSeeLogs}>
                  {t("superAdmin.contentIngestion.seeLogs", "See Logs")}
                </Button>,
                <Button key="close" onClick={handleCloseModal}>
                  {t("superAdmin.contentIngestion.close", "Close")}
                </Button>,
              ]
            : result === "error"
              ? [
                  <Button key="close" onClick={handleCloseModal}>
                    {t("superAdmin.contentIngestion.close", "Close")}
                  </Button>,
                ]
              : [
                  <Button
                    key="confirm"
                    type="primary"
                    loading={loading}
                    onClick={handleConfirmCrawl}
                  >
                    {t("superAdmin.contentIngestion.startCrawl", "Start Crawl")}
                  </Button>,
                  <Button key="cancel" onClick={handleCloseModal}>
                    {t("common.cancel", "Cancel")}
                  </Button>,
                ]
        }
      >
        {result === null && (
          <>
            <p>
              {t(
                "superAdmin.contentIngestion.startCrawlPrompt",
                "This will trigger the crawl to start. Do you want to continue?",
              )}
            </p>
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                {t(
                  "superAdmin.contentIngestion.useSonyCrawler",
                  "Use Sony Crawler:",
                )}
              </Text>
              <Radio.Group
                onChange={(e) => setUseSonyCrawler(e.target.value)}
                value={useSonyCrawler}
                style={{ marginLeft: 8 }}
              >
                <Radio value={true}>{t("common.yes", "Yes")}</Radio>
                <Radio value={false}>{t("common.no", "No")}</Radio>
              </Radio.Group>
            </div>
          </>
        )}
        {result === "success" && (
          <p style={{ color: "green" }}>
            {t(
              "superAdmin.contentIngestion.crawlStartedSuccessfully",
              "Crawl started successfully!",
            )}
          </p>
        )}
        {result === "error" && (
          <p style={{ color: "red" }}>
            {t(
              "superAdmin.contentIngestion.failedToStartCrawl",
              "Failed to start crawl",
            )}
            : {errorMessage}
          </p>
        )}
      </Modal>
      <Modal
        open={logModalVisible}
        title={t("superAdmin.contentIngestion.crawlLogs", "Crawl Logs")}
        onCancel={handleCloseLogModal}
        footer={
          <Button key="close" onClick={handleCloseLogModal}>
            {t("superAdmin.contentIngestion.close", "Close")}
          </Button>
        }
        width={700}
      >
        <div className="crawl-log-modal-body">
          {logLoading && (
            <div>
              {t(
                "superAdmin.contentIngestion.connectingToLogs",
                "Connecting to logs...",
              )}
            </div>
          )}
          <div className="crawl-log-modal-logarea">
            {logs.length === 0 && !logLoading ? (
              <div className="crawl-log-modal-empty">
                <span>
                  {t(
                    "superAdmin.contentIngestion.noLogsYet",
                    "No logs yet. Logs will appear here once the crawl starts. You can wait here or come back later by clicking the",
                  )}
                </span>
                <FileText size={14} style={{ marginLeft: 4, marginRight: 4 }} />
                <span className="crawl-log-modal-empty">
                  {t(
                    "superAdmin.contentIngestion.iconOnCrawlCard",
                    "icon on the crawl card.",
                  )}
                </span>
              </div>
            ) : (
              logs.map((log, i) => {
                const logStr =
                  typeof log === "string" ? log : JSON.stringify(log, null, 2);
                const isLong = logStr.length > 200;
                return (
                  <div key={i} className="crawl-log-modal-logline">
                    <pre className="crawl-log-modal-payload">
                      {isLong ? (
                        <>
                          {logStr.slice(0, 200)}...
                          <Button
                            type="link"
                            size="small"
                            style={{ padding: 0, marginLeft: 4 }}
                            onClick={() =>
                              setFullLogModal({
                                visible: true,
                                content: logStr,
                              })
                            }
                          >
                            {t(
                              "superAdmin.contentIngestion.showMore",
                              "Show More",
                            )}
                          </Button>
                        </>
                      ) : (
                        logStr
                      )}
                    </pre>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Modal>
      <Modal
        open={fullLogModal.visible}
        title={t(
          "superAdmin.contentIngestion.fullLogMessage",
          "Full Log Message",
        )}
        onCancel={() => setFullLogModal({ visible: false, content: "" })}
        footer={[
          <Button
            key="close"
            onClick={() => setFullLogModal({ visible: false, content: "" })}
          >
            {t("superAdmin.contentIngestion.close", "Close")}
          </Button>,
        ]}
        width={800}
      >
        <pre style={{ whiteSpace: "pre-wrap" }}>{fullLogModal.content}</pre>
      </Modal>
    </>
  );
};

CrawlConfigCard.propTypes = {
  cfg: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default CrawlConfigCard;
